/**
 * News Service
 * Manages news/announcements in Firestore for the B2B portal
 */

import { db } from '../firebase';
import {
  collection,
  getDocs,
  addDoc,
  query,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';

export interface NewsItem {
  id: string;
  category: 'Logistics' | 'Market Opportunity' | 'Industry Trends' | 'Product Launch' | 'Alert';
  date: string;
  title: string;
  summary: string;
  fullContent: string;
  createdAt: Timestamp;
  published: boolean;
  source?: 'manual' | 'agent';
}

const NEWS_COLLECTION = 'news';

/**
 * Fetch all published news items, newest first
 */
export async function getNewsItems(maxItems: number = 20): Promise<NewsItem[]> {
  const newsRef = collection(db, NEWS_COLLECTION);
  const q = query(
    newsRef,
    orderBy('createdAt', 'desc'),
    limit(maxItems)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...doc.data()
    } as NewsItem))
    .filter(item => item.published !== false);
}

/**
 * Add a new news item (used by admin or CrewAI agent)
 */
export async function addNewsItem(news: Omit<NewsItem, 'id' | 'createdAt'>): Promise<string> {
  const newsRef = collection(db, NEWS_COLLECTION);

  const docRef = await addDoc(newsRef, {
    ...news,
    createdAt: Timestamp.now(),
    published: news.published ?? true,
  });

  return docRef.id;
}

/**
 * Format date for display
 */
export function formatNewsDate(timestamp: Timestamp): string {
  const date = timestamp.toDate();
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
